import React, { useEffect, useRef, useState } from 'react';
import { FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const thumbCache = new Map(); // in-memory cache per session
let pdfjsPromise = null;

const loadPdfjs = () => {
  if (pdfjsPromise) return pdfjsPromise;
  pdfjsPromise = (async () => {
    const pdfjsLib = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/build/pdf');
    const workerMod = await import(/* webpackChunkName: "pdfjs-worker" */ 'pdfjs-dist/build/pdf.worker.entry');
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerMod.default || workerMod;
    return pdfjsLib;
  })();
  return pdfjsPromise;
};

export default function PdfThumbnail({ docId, url, alt }) {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | done | error

  useEffect(() => {
    let cancelled = false;
    const cacheKey = docId || url;

    const render = async () => {
      try {
        const pdfjsLib = await loadPdfjs();
        if (cancelled) return;

        let fetchUrl;
        if (url.startsWith('/')) {
          fetchUrl = `${BACKEND_URL}${url}`;
        } else {
          fetchUrl = `${BACKEND_URL}/api/proxy-download?url=${encodeURIComponent(url)}`;
        }

        let dataUrl = thumbCache.get(cacheKey);
        if (!dataUrl) {
          const loadingTask = pdfjsLib.getDocument({ url: fetchUrl });
          const pdf = await loadingTask.promise;
          const page = await pdf.getPage(1);
          const viewport = page.getViewport({ scale: 0.5 });

          const tmpCanvas = document.createElement('canvas');
          tmpCanvas.width = viewport.width;
          tmpCanvas.height = viewport.height;
          const ctx = tmpCanvas.getContext('2d');
          await page.render({ canvasContext: ctx, viewport }).promise;
          dataUrl = tmpCanvas.toDataURL('image/jpeg', 0.7);
          thumbCache.set(cacheKey, dataUrl);
        }

        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          setStatus('done');
        };
        img.src = dataUrl;
      } catch (err) {
        console.error('PDF thumbnail failed', err);
        if (!cancelled) setStatus('error');
      }
    };

    render();
    return () => { cancelled = true; };
  }, [docId, url]);

  return (
    <div className="relative w-full h-full bg-zinc-950 flex items-center justify-center overflow-hidden">
      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-6 w-6 rounded-full border-2 border-red-600/40 border-t-red-600 animate-spin" />
        </div>
      )}
      {status === 'error' && (
        <FileText className="h-10 w-10 text-red-600/60" aria-label={alt} />
      )}
      <canvas
        ref={canvasRef}
        className={`w-full h-full object-cover ${status === 'done' ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
        aria-label={alt}
      />
    </div>
  );
}
